import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Badge } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import LoadingSpinner from "../../components/LoadingSpinner";
import { GetDeliveryBoys } from "../../requests";

const RiderAvailability = () => {
  document.title = "Rider Availability | Susipizza";

  const [deliveryData, setDeliveryData] = useState([]);
  const [loading, setLoading] = useState(false);

  // GetDeliveryBoys is now token-scoped server-side — a store-tied admin
  // automatically gets only their own store's riders, superadmin gets all
  // of them (see requests.js / delivery.js).
  const fetchRiders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const resp = await GetDeliveryBoys(token);
      setDeliveryData(resp.data || []);
    } catch (err) {
      console.error("Failed to fetch riders for availability:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRiders();
  }, []);

  // Group riders by status
  const availableRiders = deliveryData.filter(
    (r) => (r.status || "").toLowerCase() === "available"
  );
  
  const assignedRiders = deliveryData.filter(
    (r) =>
      (r.status || "").toLowerCase() === "assigned" ||
      (r.status || "").toLowerCase() === "delivering"
  );

  const offlineRiders = deliveryData.filter(
    (r) =>
      (r.status || "").toLowerCase() === "offline" ||
      (r.status || "").toLowerCase() === "disabled" ||
      !(r.status)
  );

  const getInitials = (n) => {
    if (!n) return "R";
    const parts = n.split(" ");
    return parts.map(p => p.charAt(0)).join("").toUpperCase().slice(0, 2);
  };

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading} />
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Delivery Management" breadcrumbItem="Rider Availability" />
          
          <div className="mb-4">
            <h4>Live Rider Availability Board</h4>
            <p className="text-muted">Monitor real-time status and active assignments of all delivery riders.</p>
          </div>

          <Row>
            {/* Available Column */}
            <Col lg={4} md={12} className="mb-4">
              <Card className="border-0 shadow-sm" style={{ backgroundColor: "#f0fdf4", minHeight: "500px" }}>
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                    <h5 className="text-success m-0 font-weight-bold">
                      🟢 Available ({availableRiders.length})
                    </h5>
                    <Badge color="success" pill>Ready to Assign</Badge>
                  </div>
                  
                  {availableRiders.length > 0 ? (
                    availableRiders.map((rider) => (
                      <Card key={rider.id} className="mb-3 border-0 shadow-sm">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center gap-3">
                            {rider.photo ? (
                              <img
                                src={rider.photo}
                                alt={rider.name}
                                style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "50%",
                                  backgroundColor: "#22c55e",
                                  color: "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: "bold"
                                }}
                              >
                                {getInitials(rider.name)}
                              </div>
                            )}
                            <div>
                              <h6 className="m-0 font-weight-bold">{rider.name}</h6>
                              <small className="text-muted">
                                {rider.vehicle_type || "Bike"} • {rider.phoneNumber}
                              </small>
                              <div className="mt-1">
                                <small className="badge bg-light text-dark">
                                  Store: {Array.isArray(rider.store) ? rider.store.join(", ") : rider.store}
                                </small>
                              </div>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center p-4 text-muted border border-dashed rounded bg-white">
                      No available riders online.
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Assigned / Delivering Column */}
            <Col lg={4} md={12} className="mb-4">
              <Card className="border-0 shadow-sm" style={{ backgroundColor: "#fffbeb", minHeight: "500px" }}>
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                    <h5 className="text-warning m-0 font-weight-bold">
                      🟠 Assigned / Delivering ({assignedRiders.length})
                    </h5>
                    <Badge color="warning" pill>In Progress</Badge>
                  </div>

                  {assignedRiders.length > 0 ? (
                    assignedRiders.map((rider) => (
                      <Card key={rider.id} className="mb-3 border-0 shadow-sm">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center gap-3 mb-2">
                            {rider.photo ? (
                              <img
                                src={rider.photo}
                                alt={rider.name}
                                style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "50%",
                                  backgroundColor: "#f59e0b",
                                  color: "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: "bold"
                                }}
                              >
                                {getInitials(rider.name)}
                              </div>
                            )}
                            <div>
                              <h6 className="m-0 font-weight-bold">{rider.name}</h6>
                              <small className="text-muted">
                                {rider.vehicle_type || "Bike"} • {rider.phoneNumber}
                              </small>
                            </div>
                            <span className="ms-auto badge bg-warning-subtle text-warning text-uppercase" style={{ fontSize: "10px" }}>
                              {rider.status}
                            </span>
                          </div>

                          <div className="p-2 bg-light rounded border">
                            <div className="d-flex justify-content-between">
                              <small className="font-weight-semibold text-muted">Current Order ID:</small>
                              <small className="font-weight-bold text-dark">{rider.current_order_id || "Assigning..."}</small>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center p-4 text-muted border border-dashed rounded bg-white">
                      No active assignments currently.
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Offline / Disabled Column */}
            <Col lg={4} md={12} className="mb-4">
              <Card className="border-0 shadow-sm" style={{ backgroundColor: "#f9fafb", minHeight: "500px" }}>
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                    <h5 className="text-secondary m-0 font-weight-bold">
                      🔴 Offline / Disabled ({offlineRiders.length})
                    </h5>
                    <Badge color="secondary" pill>Inactive</Badge>
                  </div>

                  {offlineRiders.length > 0 ? (
                    offlineRiders.map((rider) => (
                      <Card key={rider.id} className="mb-3 border-0 shadow-sm" style={{ opacity: rider.status === "Disabled" ? 0.6 : 1 }}>
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center gap-3">
                            {rider.photo ? (
                              <img
                                src={rider.photo}
                                alt={rider.name}
                                style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "50%",
                                  backgroundColor: "#9ca3af",
                                  color: "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: "bold"
                                }}
                              >
                                {getInitials(rider.name)}
                              </div>
                            )}
                            <div>
                              <h6 className="m-0 font-weight-bold text-muted">{rider.name}</h6>
                              <small className="text-muted">
                                {rider.phoneNumber}
                              </small>
                              <div className="mt-1">
                                <span className={`badge ${rider.status === "Disabled" ? "bg-danger" : "bg-secondary"} text-uppercase`} style={{ fontSize: "9px" }}>
                                  {rider.status || "Offline"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center p-4 text-muted border border-dashed rounded bg-white">
                      All riders are currently online.
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default RiderAvailability;
